import React, { useState, useRef, useEffect } from "react";
import { useParams, useLocation } from "react-router-dom";
import { Search } from "lucide-react";
import { useErrorHandler } from "../../services/ErrorHandler";
import Loader from "../../components/Loader";
import ReportsFilter from "../../components/ReportsFilter";
import { displayToast } from "../../utils/Toast";
import { capitalizeFirstLetterOfEachWord } from "../../utils/Formatter";
import DropDown from "../../assets/svgIcons/PrimaryDropDown.svg";
import { fetchReportsByCode } from "../../apis/reports";
import useAutoRefresh from "../../hooks/useAutoRefresh";
import "../../styles/pages/reports/Reports.css";

const Reports = () => {
  const { formattedName } = useParams();
  const location = useLocation();
  const { handleErrorResponse } = useErrorHandler();
  const transactionCode =
    location.state?.transactionCode ||
    sessionStorage.getItem("transactionCode");
  const tab = location.state?.tab || sessionStorage.getItem("tab");

  const dropdownRef = useRef(null);
  const inputRef = useRef(null);
  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedItem, setSelectedItem] = useState("");
  const [selectedReport, setSelectedReport] = useState([]);
  const [salesReports, setSalesReports] = useState([]);

  const filteredReports = searchTerm
    ? salesReports.filter((report) =>
        report.name.toLowerCase().includes(searchTerm.toLowerCase())
      )
    : salesReports;

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target) &&
        inputRef.current !== event.target
      ) {
        setIsOpen(false);
        setSearchTerm("");
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleItemClick = (item) => {
    setSelectedReport(item);
    setSelectedItem(item.name);
    setIsOpen(false);
    setSearchTerm("");
  };

  const fetchReportsData = async (silent = false) => {
    try {
      if (!silent) setLoading(true);
      const response = await fetchReportsByCode(transactionCode, tab);
      if (response.description) {
        handleErrorResponse(response.description);
        displayToast(response.description, "error");
      } else {
        const reports = response.items || [];
        const isManualReportsCategory = formattedName
          ?.toLowerCase()
          ?.includes("manual-reports");
        const isHrReportsCategory = String(formattedName || "")
          .toLowerCase()
          .includes("hr");

        const hasContractReviewReport = reports.some((item) =>
          String(item?.name || "")
            .toLowerCase()
            .includes("contract review form"),
        );

        const hasTrainingAttendanceReport = reports.some((item) =>
          String(item?.name || "")
            .toLowerCase()
            .includes("training attendence") ||
          String(item?.name || "")
            .toLowerCase()
            .includes("training attendance"),
        );

        const hasListOfEmployeesReport = reports.some((item) =>
          String(item?.name || "")
            .toLowerCase()
            .includes("list of employees"),
        );

        const hasListOfTrainersReport = reports.some((item) =>
          String(item?.name || "")
            .toLowerCase()
            .includes("list of trainers"),
        );

        const hasHrInductionTrainingSheetReport = reports.some((item) =>
          String(item?.name || "")
            .toLowerCase()
            .includes("induction training sheet"),
        );

        const hasHrTrainingNeedsRecordReport = reports.some((item) =>
          String(item?.name || "")
            .toLowerCase()
            .includes("training needs record"),
        );

        const hasHrEmployeeHistoryCardReport = reports.some((item) =>
          String(item?.name || "")
            .toLowerCase()
            .includes("employee history card"),
        );

        if (isManualReportsCategory && !hasContractReviewReport) {
          reports.push({
            id: "manual_contract_review_form",
            name: "Contract Review Form",
            filter_data: [],
            isManualTemplate: true,
            templateCode: "CONTRACT_REVIEW_FORM",
          });
        }

        if (isManualReportsCategory && !hasTrainingAttendanceReport) {
          reports.push({
            id: "manual_training_attendance",
            name: "Training Attendence",
            filter_data: [],
            isManualTemplate: true,
            templateCode: "HT_TRAINING_ATTENDANCE",
          });
        }

        if (
          (isManualReportsCategory || isHrReportsCategory) &&
          !hasListOfEmployeesReport
        ) {
          reports.push({
            id: "manual_list_of_employees",
            name: "List Of Employees",
            filter_data: [],
            isManualTemplate: true,
            templateCode: "HR_LIST_OF_EMPLOYEES",
          });
        }

        if (
          (isManualReportsCategory || isHrReportsCategory) &&
          !hasListOfTrainersReport
        ) {
          reports.push({
            id: "manual_list_of_trainers",
            name: "List Of Trainers",
            filter_data: [],
            isManualTemplate: true,
            templateCode: "HR_LIST_OF_TRAINERS",
          });
        }

        if (
          (isManualReportsCategory || isHrReportsCategory) &&
          !hasHrInductionTrainingSheetReport
        ) {
          reports.push({
            id: "manual_hr_induction_training_sheet",
            name: "Hr Induction Training Sheet",
            filter_data: [],
            isManualTemplate: true,
            templateCode: "HR_INDUCTION_TRAINING_SHEET",
          });
        }

        if (
          (isManualReportsCategory || isHrReportsCategory) &&
          !hasHrTrainingNeedsRecordReport
        ) {
          reports.push({
            id: "manual_hr_training_needs_record",
            name: "Hr Training Needs Record",
            filter_data: [],
            isManualTemplate: true,
            templateCode: "HR_TRAINING_NEEDS_RECORD",
          });
        }

        if (
          (isManualReportsCategory || isHrReportsCategory) &&
          !hasHrEmployeeHistoryCardReport
        ) {
          reports.push({
            id: "manual_hr_employee_history_card",
            name: "Hr Employee History Card",
            filter_data: [],
            isManualTemplate: true,
            templateCode: "HR_EMPLOYEE_HISTORY_CARD",
          });
        }

        setSalesReports(reports);
        if (reports.length > 0 && !selectedItem) {
          setSelectedReport(reports[0]);
          setSelectedItem(reports[0].name);
        }
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchReportsData();
  }, []);

  useAutoRefresh(() => fetchReportsData(true));

  const handleSearchInputChange = (e) => {
    setSearchTerm(e.target.value);
    setIsOpen(true);
  };

  const handleDropdownClick = () => {
    setIsOpen(true);
    if (inputRef.current) {
      inputRef.current.focus();
    }
  };

  return (
    <div className="reports-page-container">
      {loading ? (
        <Loader />
      ) : (
        <>
          <h1 className="reports-title-category">
            {capitalizeFirstLetterOfEachWord(formattedName)}{" "}
            <span>({salesReports?.length} Reports)</span>
          </h1>

          <div className="reports-filter-container">
            <div className="reports-dropdown-container" ref={dropdownRef}>
              <button
                className="reports-dropdown-button"
                onClick={handleDropdownClick}
              >
                {isOpen ? (
                  <input
                    ref={inputRef}
                    type="text"
                    placeholder="Search reports..."
                    className="reports-search-input"
                    value={searchTerm}
                    onChange={handleSearchInputChange}
                    onClick={(e) => e.stopPropagation()}
                    autoFocus
                  />
                ) : (
                  <span
                    className={
                      selectedItem
                        ? "selected-text"
                        : "selected-text input-placeholder-color"
                    }
                  >
                    {selectedItem || "Search sales reports..."}
                  </span>
                )}
                {isOpen ? (
                  <Search className="reports-search-icon" size={18} />
                ) : (
                  <img
                    src={DropDown}
                    alt="Dropdown"
                    className="reports-dropdown-icon"
                  />
                )}
              </button>

              {isOpen && (
                <div className="reports-dropdown-list">
                  {filteredReports.map((report, index) => (
                    <div
                      key={index}
                      className={`reports-dropdown-item ${
                        selectedItem === report.name ? "selected" : ""
                      }`}
                      onClick={() => handleItemClick(report)}
                    >
                      {report.name}
                    </div>
                  ))}
                  {filteredReports.length === 0 && (
                    <div className="reports-dropdown-item">
                      No matching reports found
                    </div>
                  )}
                </div>
              )}
            </div>
            <div className="report-component-container">
              <ReportsFilter
                reportCategory={formattedName}
                report={selectedItem}
                selectedReport={selectedReport}
                tab={tab}
              />
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default Reports;
